import React, { useState, useEffect, useCallback } from 'react';
import { useMediaQuery } from "react-responsive";
import './App.css';

import SlButton from '@shoelace-style/shoelace/dist/react/button';
import SlDivider from '@shoelace-style/shoelace/dist/react/divider';
import SlDropdown from '@shoelace-style/shoelace/dist/react/dropdown';
import SlMenu from '@shoelace-style/shoelace/dist/react/menu';
import SlMenuItem from '@shoelace-style/shoelace/dist/react/menu-item';

import fullLogo from '../../assets/images/full-logo-fatturami.svg';
import { plainRetrieve, plainStore, safeRetrieve, storageRemove } from '../../utils/safeStoring';
import { getDarkModePreference } from '../../utils/themes';

import DialogSettings from '../../components/DialogSettings/DialogSettings';
import Dashboard from '../Dashboard/Dashboard';
import F24Datev from '../F24Datev/F24Datev';
import MarelliRegistri from '../MarelliRegistri/MarelliRegistri';
import LetturaDM10 from '../LetturaDM10/LetturaDM10';
import F24Summarize from '../F24Summarize/F24Summarize';
import { sidebarConf } from './sidebarConf';

import { MdOutlineSettings, MdOutlineLogout } from 'react-icons/md';
import { FaUser } from "react-icons/fa";
import { HiMenuAlt2 } from "react-icons/hi";


import '@shoelace-style/shoelace/dist/themes/light.css';
import '@shoelace-style/shoelace/dist/themes/dark.css';
import { setBasePath } from '@shoelace-style/shoelace/dist/utilities/base-path';

// override with custom styles
import '../../Styles.css';

setBasePath('https://cdn.jsdelivr.net/npm/@shoelace-style/shoelace@2.15.0/cdn/');


export const handleSignOut = () => {
    // remove all elements from local storage (except for appTheme)
    storageRemove('APIKEY_v2');
    storageRemove('userName');
    storageRemove('firstName');
    storageRemove('fontFamily');
    // redirect to sign-up page
    window.location.href = '/sign-up';
}


function App({ activePage }) {

    const isTabletScreen = useMediaQuery({ maxWidth: 1224 });
    const isMobileScreen = useMediaQuery({ maxWidth: 768 });

    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    const [showDialogSettings, setShowDialogSettings] = useState(false);

    const appTheme = plainRetrieve('appTheme') || 'system';
    const systemDarkMode = useMediaQuery({ query: '(prefers-color-scheme: dark)' });
    const [isDarkMode, setIsDarkMode] = useState(getDarkModePreference(appTheme, systemDarkMode));


    // if there is no saved APIKEY, redirect to login page
    useEffect(() => {
        const apiKey = safeRetrieve('APIKEY_v2') || '';
        if (apiKey.length === 0) {
        window.location.href = '/sign-up';
        }
    }, []);


    const handleSettingsButtonClick = useCallback(() => setShowDialogSettings(true), []);

    const handleChangeAppTheme = (theme) => {
        const useDarkMode = getDarkModePreference(theme, systemDarkMode);
        setIsDarkMode(useDarkMode);
    }

    const handleDyslexiaFont = (checked) => {
        if (checked) {
            // set dyslexia friendly font family
            plainStore('fontFamily', 'open-dyslexic');
            document.documentElement.style.setProperty('--font-family', 'var(--dyslexia-font-family)');
        } else {
            // set default font family
            plainStore('fontFamily', 'default');
            document.documentElement.style.setProperty('--font-family', 'var(--default-font-family)');
        }
    }

    // initialize font
    useEffect(() => {
        const useDyslexiaFont = (plainRetrieve('fontFamily') === 'open-dyslexic') || false;
        handleDyslexiaFont(useDyslexiaFont);
    }, []);


    const toggleMobileSidebar = () => { setIsMobileSidebarOpen(!isMobileSidebarOpen) };

    const handleNavClick = () => {
        // close the sidebar on mobile screens
        if (window.innerWidth < 768) {
            setIsMobileSidebarOpen(false);
        }
    };


    return (
        <div className={isDarkMode ? 'dark-mode sl-theme-dark sl-theme-dark-fatturami' : 'sl-theme-light sl-theme-fatturami'} style={{ height: 'inherit', minHeight: 'inherit' }}>

        <div className="App">

            {/* Sidebar */}
            <div className={`sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}`}>
                
                <div className="sidebar-header">
                    <a className='logo-container' href="/">
                        <img src={fullLogo} className="App-logo" alt="Logo" />
                    </a>
                </div>

                <nav className="sidebar-nav">

                    {sidebarConf.map((item, index) => (
                        <a 
                            href={item.path} 
                            onClick={handleNavClick} 
                            key={index} 
                            className={`nav-item ${(activePage === item.pageName) ? 'active' : ''}`}
                        >
                            {item.icon}
                            <span className="label">{item.label}</span>
                        </a>
                    ))}

                </nav>

            </div>

            {isMobileSidebarOpen && (
                <div className="sidebar-backdrop-layer" onClick={() => setIsMobileSidebarOpen(false)} />
            )}


            {/* Main Content */}
            <div className='App-main-content'>

            <header className="App-header sticky">

                {isMobileScreen && (
                    <button onClick={toggleMobileSidebar} >
                        <HiMenuAlt2 style={{ fontSize: '20px' }} />
                    </button>
                )}

                <SlDropdown placement="bottom-end" distance={4}>
                    <SlButton slot="trigger" className="dropdown-user" size="medium" caret>
                        {isMobileScreen ? (<FaUser />) : <span>{plainRetrieve("userName")}</span>}
                    </SlButton>
                    <SlMenu>
                        <SlMenuItem onClick={handleSettingsButtonClick}>
                        <MdOutlineSettings className='icon'/>
                        Impostazioni
                        </SlMenuItem>
                        <SlDivider />
                        <SlMenuItem className='logout' onClick={handleSignOut}>
                        <MdOutlineLogout className='icon'/>
                        Disconnetti account
                        </SlMenuItem>
                    </SlMenu>
                </SlDropdown>

            </header>


            <DialogSettings 
            isOpen={showDialogSettings}
            setIsOpen={setShowDialogSettings}
            onChangeAppTheme={handleChangeAppTheme}
            onChangeDyslexiaFont={handleDyslexiaFont}
            />


            <div className="App-body">
                <div className="demo-content">

                    {activePage === 'dashboard' && <Dashboard />}
                    {activePage === 'f24-extraction' && <F24Datev />}
                    {activePage === 'marelli-registri-iva' && <MarelliRegistri />}
                    {activePage === 'dm10-reading' && <LetturaDM10 />}
                    {activePage === 'f24-summarize' && <F24Summarize />}
                
                </div>
            </div>
            
            </div>

            {/* <footer>
            <img src={isMobileScreen ? baseLogo : fullLogo} alt='Logo' style={{ height: "-webkit-fill-available" }} />
            <span>Copyright © 2024 Rafiki</span>
            </footer> */}
        
        </div>
        
        </div>
    );
}

export default App;
